// Shared shell for the ticket-detail right rail.
//
// Every rail panel (Watchers, Notes, Attachments, From this contact,
// Activity) used to hand-roll its own card, its own heading style and — in
// one case — its own `max-h-72 overflow-y-auto`. On a long thread the rail
// became an endless scroll with nested scrollbars. This gives them one
// look and one behaviour: a height cap with a "Show more / Show less"
// toggle that only appears when the content really overflows.
//
// `CappedBody` is exported on its own for panels whose body is split
// across Radix tab panels (Attachments), where the cap has to live inside
// each tab rather than around the tab strip.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CappedBodyProps {
  /** Collapsed height cap, in rem. */
  maxHeightRem: number;
  children: React.ReactNode;
  className?: string;
}

export function CappedBody({ maxHeightRem, children, className }: CappedBodyProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const bodyId = useId();
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const measure = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    setOverflowing(el.offsetHeight > maxHeightRem * rootPx + 1);
  }, [maxHeightRem]);

  // useLayoutEffect so the first paint already knows whether to show the
  // toggle — no flash of an uncapped list that then snaps shut.
  useLayoutEffect(() => {
    measure();
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, [measure]);

  // Content shrank below the cap while expanded (e.g. a note was deleted):
  // reset so the next time it grows it starts collapsed again.
  useEffect(() => {
    if (!overflowing && expanded) setExpanded(false);
  }, [overflowing, expanded]);

  const capped = overflowing && !expanded;

  function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (!next) {
      // Collapsing a tall section leaves the viewport parked far below
      // its header — pull the section back into view.
      requestAnimationFrame(() => {
        const host = wrapRef.current?.closest('[data-rail-section]') ?? wrapRef.current;
        host?.scrollIntoView({ block: 'nearest' });
      });
    }
  }

  return (
    <div ref={wrapRef} className={className}>
      <div
        id={bodyId}
        className={cn('relative', capped && 'overflow-hidden')}
        style={capped ? { maxHeight: `${maxHeightRem}rem` } : undefined}
      >
        <div ref={innerRef}>{children}</div>
        {capped ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background to-transparent"
          />
        ) : null}
      </div>
      {overflowing ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          className="mt-1 flex w-full items-center justify-center gap-1 rounded-md py-1.5 text-2xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {expanded ? 'Show less' : 'Show more'}
          <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
        </button>
      ) : null}
    </div>
  );
}

interface RailSectionProps {
  title: string;
  icon?: LucideIcon;
  /** Small count shown next to the title. */
  count?: number | string;
  /** Right-aligned header control (e.g. the WatcherPicker). */
  action?: React.ReactNode;
  /** Uncapped content between the header and the capped body — tab strips,
   *  composers. Never clipped. */
  pre?: React.ReactNode;
  /** Content below the capped body — never clipped (e.g. a "view all" link). */
  footer?: React.ReactNode;
  /** Collapsed cap for `children`, in rem. Omit for an uncapped body. */
  collapsedMaxHeight?: number;
  children?: React.ReactNode;
  className?: string;
}

export function RailSection({
  title,
  icon: Icon,
  count,
  action,
  pre,
  footer,
  collapsedMaxHeight,
  children,
  className,
}: RailSectionProps) {
  return (
    <section
      data-rail-section
      className={cn('min-w-0 rounded-md border border-border/70 bg-background p-3', className)}
    >
      <header className="mb-2 flex min-w-0 items-center justify-between gap-2">
        <h3 className="flex min-w-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
          <span className="truncate">{title}</span>
          {count !== undefined ? (
            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-3xs font-semibold normal-case tracking-normal text-foreground">
              {count}
            </span>
          ) : null}
        </h3>
        {action ? <div className="shrink-0">{action}</div> : null}
      </header>
      {pre}
      {children !== undefined && children !== null ? (
        collapsedMaxHeight ? (
          <CappedBody maxHeightRem={collapsedMaxHeight}>{children}</CappedBody>
        ) : (
          children
        )
      ) : null}
      {footer}
    </section>
  );
}
